import React, { useState, useRef } from 'react';
import { useSwipeable } from 'react-swipeable';
import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import Icon from '../AppIcon';

const SwipeableCard = ({ 
  children, 
  onDelete, 
  onArchive, 
  onEdit,
  disabled = false,
  className = ''
}) => {
  const [swipeDirection, setSwipeDirection] = useState(null);
  const x = useMotionValue(0);
  const cardRef = useRef(null);

  const SWIPE_THRESHOLD = 120;
  const HAPTIC_THRESHOLD = 60;
  const hasVibratedRef = useRef(false);

  const triggerHaptic = () => {
    if ('vibrate' in navigator && !hasVibratedRef?.current) {
      navigator.vibrate(10);
      hasVibratedRef.current = true;
    }
  };

  const backgroundOpacity = useTransform(
    x,
    [-SWIPE_THRESHOLD, -HAPTIC_THRESHOLD, 0, HAPTIC_THRESHOLD, SWIPE_THRESHOLD],
    [1, 0.5, 0, 0.5, 1]
  );

  const leftActionScale = useTransform(
    x,
    [-SWIPE_THRESHOLD, 0],
    [1, 0.5]
  );

  const rightActionScale = useTransform(
    x,
    [0, SWIPE_THRESHOLD],
    [0.5, 1]
  );

  const handlers = useSwipeable({
    onSwiping: (eventData) => {
      if (disabled) return;
      
      const deltaX = eventData?.deltaX;
      x?.set(deltaX);

      if (Math.abs(deltaX) > HAPTIC_THRESHOLD) {
        triggerHaptic();
      }

      if (deltaX < -HAPTIC_THRESHOLD) {
        setSwipeDirection('left');
      } else if (deltaX > HAPTIC_THRESHOLD) {
        setSwipeDirection('right');
      } else {
        setSwipeDirection(null);
      }
    },
    onSwiped: (eventData) => {
      if (disabled) return;
      
      const deltaX = eventData?.deltaX;
      hasVibratedRef.current = false;

      if (deltaX < -SWIPE_THRESHOLD) {
        if ('vibrate' in navigator) navigator.vibrate(20);
        onDelete?.();
      } else if (deltaX > SWIPE_THRESHOLD) {
        if ('vibrate' in navigator) navigator.vibrate(20);
        onEdit?.();
      } else if (deltaX < -HAPTIC_THRESHOLD && deltaX > -SWIPE_THRESHOLD) {
        if ('vibrate' in navigator) navigator.vibrate(15);
        onArchive?.();
      }

      animate(x, 0, { type: 'spring', stiffness: 300, damping: 30 });
      setSwipeDirection(null);
    },
    trackMouse: false,
    trackTouch: true,
    preventScrollOnSwipe: true,
    delta: 10
  });

  return (
    <div className={`relative overflow-hidden ${className}`} ref={cardRef}>
      {/* Left Action Background (Delete) */}
      <motion.div
        className="absolute inset-0 bg-error flex items-center justify-end px-6"
        style={{ opacity: backgroundOpacity }}
      >
        <motion.div style={{ scale: leftActionScale }}>
          <Icon name="Trash2" size={24} color="white" />
        </motion.div>
      </motion.div>
      {/* Right Action Background (Edit) */}
      <motion.div
        className="absolute inset-0 bg-warning flex items-center justify-start px-6"
        style={{ opacity: backgroundOpacity }}
      >
        <motion.div style={{ scale: rightActionScale }}>
          <Icon name="Edit2" size={24} color="white" />
        </motion.div>
      </motion.div>
      {/* Archive Indicator (Middle swipe left) */}
      {swipeDirection === 'left' && Math.abs(x?.get()) < SWIPE_THRESHOLD && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="absolute inset-0 bg-primary/80 flex items-center justify-center"
        >
          <Icon name="Archive" size={24} color="white" />
        </motion.div>
      )}
      {/* Swipeable Card Content */}
      <motion.div
        {...handlers}
        style={{ x }}
        drag={disabled ? false : 'x'}
        dragConstraints={{ left: -SWIPE_THRESHOLD * 1.2, right: SWIPE_THRESHOLD * 1.2 }}
        dragElastic={0.2}
        onDragEnd={(event, info) => {
          if (disabled) return;
          
          const deltaX = info?.offset?.x;
          hasVibratedRef.current = false;

          if (deltaX < -SWIPE_THRESHOLD) {
            if ('vibrate' in navigator) navigator.vibrate(20);
            onDelete?.();
          } else if (deltaX > SWIPE_THRESHOLD) {
            if ('vibrate' in navigator) navigator.vibrate(20);
            onEdit?.();
          } else if (deltaX < -HAPTIC_THRESHOLD && deltaX > -SWIPE_THRESHOLD) {
            if ('vibrate' in navigator) navigator.vibrate(15);
            onArchive?.();
          }

          animate(x, 0, { type: 'spring', stiffness: 300, damping: 30 });
          setSwipeDirection(null);
        }}
        className="relative bg-card cursor-grab active:cursor-grabbing touch-pan-y"
      >
        {children}
      </motion.div>
    </div>
  );
};

export default SwipeableCard;